import { useState } from "react";
import { Camera, Clock, MapPin, Shield, X, AlertTriangle, Check } from "lucide-react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Card } from "../../components/shared/Card";
import { Btn } from "../../components/shared/Btn";
import { Badge } from "../../components/shared/Badge";
import { SelfieCaptureModal } from "../../components/shared/SelfieCaptureModal";
import { MonthPicker } from "../../components/shared/MonthPicker";
import { useAppData } from "../../lib/AppDataContext";
import { useAuth } from "../../lib/AuthContext";
import {
  ATTENDANCE_STATUS_META, UNPLANNED_ABSENCE_PENALTY, isAutoAbsent, withAutoAbsent,
  deriveStatusFromClockIn, formatDurationMs,
  summariseAttendance, computePayroll, inr,
} from "../../lib/attendance";

/* My Attendance — sales-person self-service. Selfie required for Clock In
   and Clock Out (two separate selfies). Location text mandatory. Half-day
   auto-derived from office start + grace. */
export function MyAttendanceView() {
  const {
    allSalesPeople, attendanceLog, monthlyLogByPerson, markAttendance,
    clockInSelf, clockOutSelf, markAbsentSelf,
    officeTiming, month, setMonth,
    staffSalaries, salaryAdvances,
    leadsData,
  } = useAppData();

  // Phase 5 Bundle A — derive ME from the logged-in user's name/email.
  // Falls back to first emp for super-admin views (when no match).
  const { user } = useAuth();
  const me = allSalesPeople.find(
    (p) =>
      (user?.email && (p.email === user.email)) ||
      (user?.name && p.name === user.name),
  ) || allSalesPeople[0];
  const ME_NAME = me?.name || user?.name || "—";

  const myLog = monthlyLogByPerson[ME_NAME] || [];
  const today = new Date();
  const todayKey = today.toISOString().split("T")[0];
  const fullLog = attendanceLog[ME_NAME] || [];
  const todayRow = fullLog.find((r) => r.date === todayKey);

  const [cameraMode, setCameraMode] = useState(null); // 'in' | 'out' | null
  const [absentOpen, setAbsentOpen] = useState(false);
  const [absentReason, setAbsentReason] = useState("Personal");
  const [preview, setPreview] = useState(null);

  const recent = withAutoAbsent(myLog, officeTiming?.weekendDay ?? 0, officeTiming?.endHHMM, month, me?.profile?.joiningDate)
    .sort((a, b) => b.date.localeCompare(a.date)).slice(0, 30);

  const summary = summariseAttendance(myLog, month, officeTiming?.weekendDay ?? 0, officeTiming?.endHHMM, me?.profile?.joiningDate);
  const payroll = computePayroll(summary);
  const mePayroll = staffSalaries.find((s) => s.name === ME_NAME);
  const myIncentivePaid = mePayroll?.skydreamsIncentivePaid || 0;
  const myIncentivePending = mePayroll?.skydreamsIncentivePending || 0;
  const myBuilderIncentivePaid = mePayroll?.builderIncentivePaid || 0;
  const myBuilderIncentivePending = mePayroll?.builderIncentivePending || 0;
  const myAdvances = (salaryAdvances || []).filter((a) => a.employeeName === ME_NAME || a.employeeId === me?.id);
  const myAdvanceTotal = mePayroll?.advanceTotal || 0;
  const myNetAfterAdvance = mePayroll?.netAfterAdvance ?? payroll.net;
  const myTotalEarning = mePayroll?.totalEarning ?? payroll.net;

  const onClockIn = async (dataUrl, hhmm, location) => {
    try {
      await clockInSelf({
        date: todayKey,
        clockInAt: new Date().toISOString(),
        selfieDataUrl: dataUrl,
        location,
      });
    } catch (e) {
      console.error("clockIn failed", e);
      alert("Clock-in failed: " + (e?.message || "Unknown error"));
    } finally {
      setCameraMode(null);
    }
  };
  const onClockOut = async (dataUrl, hhmm, location) => {
    try {
      await clockOutSelf({
        date: todayKey,
        clockOutAt: new Date().toISOString(),
        selfieDataUrl: dataUrl,
        location,
      });
    } catch (e) {
      console.error("clockOut failed", e);
      alert("Clock-out failed: " + (e?.message || "Unknown error"));
    } finally {
      setCameraMode(null);
    }
  };
  const submitAbsent = async () => {
    try {
      await markAbsentSelf({ date: todayKey, reason: absentReason });
    } catch (e) {
      console.error("markAbsentSelf failed", e);
      alert("Mark absent failed: " + (e?.message || "Unknown error"));
    } finally {
      setAbsentOpen(false);
    }
  };

  const totalHoursToday = (() => {
    if (!todayRow?.clockInAt || !todayRow?.clockOutAt) return null;
    return formatDurationMs(new Date(todayRow.clockOutAt) - new Date(todayRow.clockInAt));
  })();
  const todayAuto = !todayRow && isAutoAbsent(todayKey, officeTiming?.weekendDay ?? 0, officeTiming?.endHHMM, me?.profile?.joiningDate);
  const todayMeta = todayRow ? ATTENDANCE_STATUS_META[todayRow.status] : (todayAuto ? ATTENDANCE_STATUS_META.absent : null);

  return (
    <div>
      {preview && (
        <div className="fixed inset-0 z-[60] bg-slate-900/80 grid place-items-center p-6" onClick={() => setPreview(null)}>
          <img src={preview} alt="Selfie preview" className="max-w-[90vw] max-h-[85vh] rounded-lg ring-1 ring-white/20" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
      <PageHeader accent="ops" title="My Attendance" subtitle={ME_NAME} />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-slate-500">Viewing month:</span>
        <MonthPicker value={month} onChange={setMonth} align="left" />
        <span className="text-xs text-slate-400">Switch months to see past attendance &amp; payroll</span>
      </div>

      {/* Today's mark card — the primary action */}
      <Card className="p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              Today · {today.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            {todayRow ? (
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg ring-1 ring-inset ${todayMeta.cls}`}>
                  <span className={`w-2 h-2 rounded-full ${todayMeta.dot}`} />{todayMeta.label}
                </span>
                {todayRow.clockInTime && (
                  <span className="text-sm text-slate-600 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    In <span className="font-semibold tabular-nums">{todayRow.clockInTime}</span>
                  </span>
                )}
                {todayRow.clockOutTime && (
                  <span className="text-sm text-slate-600 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    Out <span className="font-semibold tabular-nums">{todayRow.clockOutTime}</span>
                  </span>
                )}
                {totalHoursToday && (
                  <span className="text-sm font-semibold text-blue-700 bg-blue-50 ring-1 ring-inset ring-blue-600/20 rounded px-2 py-0.5">
                    Total: {totalHoursToday}
                  </span>
                )}
                {(todayRow.clockInLocation || todayRow.location) && (
                  <span className="text-sm text-slate-600 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    {todayRow.clockInLocation || todayRow.location}
                  </span>
                )}
                {todayRow.reason && <span className="text-sm text-slate-500">Reason: {todayRow.reason}</span>}
              </div>
            ) : (
              <div className="mt-2 text-sm text-slate-600">You haven't marked yourself yet today.</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(todayRow?.clockInSelfie || todayRow?.selfie) && (
              <div className="text-center">
                <img src={todayRow.clockInSelfie || todayRow.selfie} alt="Clock-in selfie"
                  onClick={() => setPreview(todayRow.clockInSelfie || todayRow.selfie)}
                  className="w-20 h-20 rounded-lg object-cover ring-1 ring-slate-200 cursor-pointer hover:ring-blue-400" />
                <div className="text-[10px] text-slate-400 mt-1">Clock-in</div>
              </div>
            )}
            {todayRow?.clockOutSelfie && (
              <div className="text-center">
                <img src={todayRow.clockOutSelfie} alt="Clock-out selfie"
                  onClick={() => setPreview(todayRow.clockOutSelfie)}
                  className="w-20 h-20 rounded-lg object-cover ring-1 ring-slate-200 cursor-pointer hover:ring-blue-400" />
                <div className="text-[10px] text-slate-400 mt-1">Clock-out</div>
              </div>
            )}
          </div>
        </div>
        {!todayRow && (
          <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-slate-100">
            <Btn variant="primary" onClick={() => setCameraMode("in")}>
              <Camera className="w-4 h-4" />Clock In (selfie)
            </Btn>
            <Btn variant="secondary" onClick={() => setAbsentOpen(true)}>
              <X className="w-4 h-4" />Mark Absent
            </Btn>
          </div>
        )}
        {todayRow && todayRow.clockInAt && !todayRow.clockOutAt && (
          <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-slate-100">
            <Btn variant="primary" onClick={() => setCameraMode("out")}>
              <Camera className="w-4 h-4" />Clock Out (selfie)
            </Btn>
            <span className="text-xs text-slate-500 self-center">Capture another selfie and current location to clock out.</span>
          </div>
        )}
        {todayRow && (todayRow.clockOutAt || !todayRow.clockInAt) && (
          <div className="text-xs text-slate-400 mt-4 pt-4 border-t border-slate-100 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            {todayRow.clockOutAt ? "Today is closed out." : "Today's attendance is locked. Contact admin for changes."}
          </div>
        )}
      </Card>

      {todayRow?.status === "half_day" && (
        <Card tone="warning" className="p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">Marked half-day.</span> Clock-in was more than {officeTiming.graceMinutes} minutes after the office start ({officeTiming.startHHMM}). This day pays at 50% of the per-day rate.
            </div>
          </div>
        </Card>
      )}

      {/* 5 status KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {/* Attendance states map cleanly onto the tone set: present is good,
            half day needs noting, absent is the one that costs money and a
            penalty. Leave and paid leave are neither good nor bad, so they
            take non-semantic hues rather than green or red. */}
        <Card tone="success" className="p-4"><div className="text-xs font-medium text-slate-600">Present</div><div className="text-2xl font-bold text-emerald-700 tabular-nums mt-1">{summary.present}</div></Card>
        <Card tone="warning" className="p-4"><div className="text-xs font-medium text-slate-600">Half Day</div><div className="text-2xl font-bold text-amber-700 tabular-nums mt-1">{summary.half_day}</div></Card>
        <Card tone="danger" className="p-4"><div className="text-xs font-medium text-slate-600">Absent</div><div className="text-2xl font-bold text-red-700 tabular-nums mt-1">{summary.absent}</div></Card>
        <Card tone="info" className="p-4"><div className="text-xs font-medium text-slate-600">Leave</div><div className="text-2xl font-bold text-blue-700 tabular-nums mt-1">{summary.leave}</div></Card>
        <Card tone="violet" className="p-4"><div className="text-xs font-medium text-slate-600">Paid Leave</div><div className="text-2xl font-bold text-violet-700 tabular-nums mt-1">{summary.paid_leave}</div></Card>
      </div>

      {/* My advances list */}
      {myAdvances.length > 0 && (
        <Card className="mb-6">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">My Advances · {month}</h4>
              <p className="text-xs text-slate-500 mt-0.5">Salary you've pulled forward this month · deducted from your net pay</p>
            </div>
            <Badge cls="bg-red-50 text-red-700 ring-red-600/20 tabular-nums">−{inr(myAdvanceTotal)}</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-3 py-2.5">Note</th>
                  <th className="px-3 py-2.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {myAdvances.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2.5 text-slate-600 tabular-nums">{a.date}</td>
                    <td className="px-3 py-2.5 text-slate-600 text-xs">{a.note || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-red-700">−{inr(a.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Recent history */}
      <h3 className="text-sm font-semibold text-slate-900 mb-3">Recent history</h3>
      <Card>
        {recent.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No attendance history yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {recent.map((r) => {
              const meta = ATTENDANCE_STATUS_META[r.status];
              const total = r.clockInAt && r.clockOutAt
                ? formatDurationMs(new Date(r.clockOutAt) - new Date(r.clockInAt)) : null;
              return (
                <div key={r.date} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
                  <div className="w-14 text-xs text-slate-500 tabular-nums shrink-0">
                    {new Date(r.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ring-1 ring-inset ${meta.cls}`}>{meta.label}</span>
                  {r.clockInTime && <span className="text-xs text-slate-500 tabular-nums">In {r.clockInTime}</span>}
                  {r.clockOutTime && <span className="text-xs text-slate-500 tabular-nums">Out {r.clockOutTime}</span>}
                  {total && <span className="text-xs font-semibold text-blue-700">{total}</span>}
                  {(r.clockInLocation || r.location) && (
                    <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400" />{r.clockInLocation || r.location}
                    </span>
                  )}
                  {r.reason && <span className="text-xs text-slate-500">— {r.reason}</span>}
                  {(r.clockInSelfie || r.selfie) && (
                    <img src={r.clockInSelfie || r.selfie} alt="Selfie"
                      onClick={() => setPreview(r.clockInSelfie || r.selfie)}
                      className="ml-auto w-8 h-8 rounded object-cover ring-1 ring-slate-200 cursor-pointer hover:ring-blue-400" />
                  )}
                  {r.markedBy === "admin" && (
                    <Badge cls="bg-slate-100 text-slate-500 ring-slate-400/20 text-[10px]">Admin set</Badge>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <SelfieCaptureModal
        open={cameraMode !== null}
        onClose={() => setCameraMode(null)}
        onCapture={(dataUrl, hhmm, location) => {
          if (cameraMode === "in") onClockIn(dataUrl, hhmm, location);
          else if (cameraMode === "out") onClockOut(dataUrl, hhmm, location);
        }}
      />

      {absentOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setAbsentOpen(false)}>
          <Card className="w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900">Mark absent today</h3>
              <button onClick={() => setAbsentOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Unplanned absence deducts the day's pay. For planned leave, ask admin to mark it instead.
            </p>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Reason</label>
            <select value={absentReason} onChange={(e) => setAbsentReason(e.target.value)}
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-blue-500 mb-4">
              <option>Personal</option><option>Sick</option><option>Family</option><option>Other</option>
            </select>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setAbsentOpen(false)}>Cancel</Btn>
              <Btn variant="danger" onClick={submitAbsent}>Confirm absent</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
